import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, ReactNode, use, useState } from "react";

import * as ComparisonToggleTracker from "@/services/tracking/trackers/feature/comparisonToggle.tracker";

type ProductComparisonBarContextValue = {
    open: boolean;
    products: Array<Product<ProductFieldSet.ProductTile>>;
    clear: () => void;
    remove: (sku: string) => void;
    toggle: () => void;
    close: () => void;
};

type ProductComparisonBarContextProviderProps = {
    products: Array<Product<ProductFieldSet.ProductTile>>;
    clear: () => void;
    remove: (sku: string) => void;
    children: ReactNode;
};

export const ProductComparisonBarContext = createContext<
    ProductComparisonBarContextValue | undefined
>(undefined);
ProductComparisonBarContext.displayName = "ProductComparisonBarContext";

export const ProductComparisonBarContextProvider = ({
    products,
    clear,
    remove,
    children
}: ProductComparisonBarContextProviderProps) => {
    const [open, setOpen] = useState(false);

    const toggle = () => {
        setOpen((prevOpen) => {
            if (prevOpen) {
                ComparisonToggleTracker.trackClose();
            } else {
                ComparisonToggleTracker.trackOpen();
            }

            return !prevOpen;
        });
    };

    const close = () => {
        ComparisonToggleTracker.trackClose();

        setOpen(false);
    };

    return (
        <ProductComparisonBarContext
            value={{
                open,
                products,
                clear,
                remove,
                toggle,
                close
            }}
        >
            {children}
        </ProductComparisonBarContext>
    );
};

export const useProductComparisonBarContext = () => {
    const context = use<ProductComparisonBarContextValue | undefined>(
        ProductComparisonBarContext
    );

    if (!isDefined(context)) {
        throw new Error(
            `${ProductComparisonBarContext.displayName} is undefined.`
        );
    }

    return context;
};
